import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import TerritoryFilters from "@/components/base/TerritoryFilters";
import { matchesTerritory, type TerritorySelection } from "@/config/territory";
import StatCard from "@/pages/proprietaire/components/StatCard";
import PlatformImmeubleForm from "@/pages/super-admin/components/PlatformImmeubleForm";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { PLATFORM_SCOPE_LABELS, type PlatformImmeuble, type PlatformScope } from "@/store/platform/types";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

const EMPTY_TERRITORY: TerritorySelection = { region: "", province: "", commune: "", secteur: "" };

export default function SuperAdminImmeublesPage() {
  const { immeubles, biensByImmeuble, removeImmeuble } = usePlatformData();

  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [territory, setTerritory] = useState<TerritorySelection>(EMPTY_TERRITORY);
  const [editing, setEditing] = useState<PlatformImmeuble | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlatformImmeuble | null>(null);

  const scopeOptions = [
    { value: "", label: "Tous les espaces" },
    ...(Object.keys(PLATFORM_SCOPE_LABELS) as PlatformScope[]).map((scope) => ({
      value: scope,
      label: PLATFORM_SCOPE_LABELS[scope],
    })),
  ];

  const stats = useMemo(
    () => ({
      total: immeubles.length,
      proprietaires: immeubles.filter((item) => item.scope === "proprietaire").length,
      agences: immeubles.filter((item) => item.scope === "agence").length,
      biens: immeubles.reduce((sum, item) => sum + biensByImmeuble(item.id).length, 0),
    }),
    [immeubles, biensByImmeuble]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = immeubles.filter((item) => {
      const matchesScope = !scopeFilter || item.scope === scopeFilter;
      const matchesSearch =
        !term ||
        [item.nom, item.region, item.province, item.commune, item.secteur, item.adresse]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesScope && matchesSearch && matchesTerritory(item, territory);
    });
    return result.sort((a, b) =>
      sort === "nom" ? a.nom.localeCompare(b.nom, "fr") : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [immeubles, search, scopeFilter, sort, territory]);

  const isFiltering =
    Boolean(search.trim()) ||
    Boolean(scopeFilter) ||
    Boolean(territory.region || territory.province || territory.commune || territory.secteur);

  const exportColumns: ExportColumn<PlatformImmeuble>[] = [
    { header: "Nom", value: (item) => item.nom },
    { header: "Espace", value: (item) => PLATFORM_SCOPE_LABELS[item.scope] },
    { header: "Région", value: (item) => item.region },
    { header: "Province", value: (item) => item.province },
    { header: "Commune / Ville", value: (item) => item.commune },
    { header: "Secteur / Quartier", value: (item) => item.secteur },
    { header: "Adresse", value: (item) => item.adresse },
    { header: "Niveaux", value: (item) => item.niveaux },
    { header: "Biens", value: (item) => biensByImmeuble(item.id).length },
    { header: "Créé le", value: (item) => formatDate(item.createdAt) },
  ];

  const pendingBiens = pendingDelete ? biensByImmeuble(pendingDelete.id).length : 0;

  const resetFilters = () => {
    setSearch("");
    setScopeFilter("");
    setTerritory(EMPTY_TERRITORY);
  };

  const columns: DataTableColumn<PlatformImmeuble>[] = [
    {
      key: "immeuble",
      header: "Immeuble",
      className: "min-w-[240px]",
      render: (immeuble) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-100 text-foreground-400">
            {immeuble.photo ? (
              <img src={immeuble.photo} alt={immeuble.nom} className="h-9 w-9 object-cover object-top" />
            ) : (
              <i className="ri-building-2-line text-lg" aria-hidden="true" />
            )}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-label font-medium text-foreground-950">{immeuble.nom}</span>
            {immeuble.adresse && <span className="truncate text-xs text-foreground-500">{immeuble.adresse}</span>}
          </div>
        </div>
      ),
    },
    {
      key: "scope",
      header: "Espace",
      render: (immeuble) => (
        <Badge tone={immeuble.scope === "agence" ? "accent" : "primary"}>
          {PLATFORM_SCOPE_LABELS[immeuble.scope]}
        </Badge>
      ),
    },
    {
      key: "region",
      header: "Région",
      className: "min-w-[130px]",
      render: (immeuble) => <span className="text-foreground-700">{immeuble.region || "—"}</span>,
    },
    {
      key: "commune",
      header: "Commune",
      className: "min-w-[130px]",
      render: (immeuble) => <span className="text-foreground-700">{immeuble.commune || "—"}</span>,
    },
    {
      key: "niveaux",
      header: "Niveaux",
      align: "center",
      className: "min-w-[90px]",
      render: (immeuble) => <span className="text-foreground-700">{immeuble.niveaux}</span>,
    },
    {
      key: "biens",
      header: "Biens",
      align: "center",
      className: "min-w-[80px]",
      render: (immeuble) => (
        <span className="font-label font-medium text-foreground-950">{biensByImmeuble(immeuble.id).length}</span>
      ),
    },
    {
      key: "createdAt",
      header: "Créé le",
      className: "min-w-[130px]",
      render: (immeuble) => <span className="text-foreground-700">{formatDate(immeuble.createdAt)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[200px]",
      render: (immeuble) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/super-admin/immeubles/${immeuble.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions onEdit={() => setEditing(immeuble)} onDelete={() => setPendingDelete(immeuble)} />
        </div>
      ),
    },
  ];

  const confirmDelete = () => {
    if (!pendingDelete) return;
    removeImmeuble(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Immeubles</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Vue globale de tous les immeubles de la plateforme, tous espaces confondus (Propriétaire et Agence).
            Consultez, corrigez ou supprimez un immeuble — la création se fait depuis l'espace concerné.
          </p>
        </div>
        <ExportMenu
          rows={filtered}
          columns={exportColumns}
          filename="super-admin-immeubles"
          title="Immeubles"
          subtitle="Immeubles affichés selon les filtres"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-building-2-line" label="Immeubles" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-user-star-line" label="Espace Propriétaire" value={String(stats.proprietaires)} tone="secondary" />
        <StatCard icon="ri-building-4-line" label="Espace Agence" value={String(stats.agences)} tone="accent" />
        <StatCard icon="ri-home-4-line" label="Biens rattachés" value={String(stats.biens)} tone="neutral" />
      </div>

      {immeubles.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <TerritoryFilters idPrefix="sa-imm" value={territory} onChange={setTerritory} className="mb-4" />
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-2xl">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sa-imm-search" className="text-xs font-label font-medium text-foreground-700">
                  Recherche
                </label>
                <Input
                  id="sa-imm-search"
                  icon="ri-search-line"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nom, commune, adresse..."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sa-imm-scope" className="text-xs font-label font-medium text-foreground-700">
                  Espace
                </label>
                <Select
                  id="sa-imm-scope"
                  value={scopeFilter}
                  onChange={(event) => setScopeFilter(event.target.value)}
                  options={scopeOptions}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 sm:w-52">
              <label htmlFor="sa-imm-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="sa-imm-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} immeuble{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {immeubles.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {immeubles.length === 0 && (
        <EmptyState
          icon="ri-building-2-line"
          title="Aucun immeuble"
          description="Les immeubles créés par les propriétaires et les agences apparaîtront ici automatiquement."
        />
      )}

      {immeubles.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun immeuble ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(immeuble) => immeuble.id}
          minWidthClass="min-w-[1300px]"
          caption="Immeubles"
        />
      )}

      <PlatformImmeubleForm open={Boolean(editing)} onClose={() => setEditing(null)} immeuble={editing} />

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        description={
          pendingDelete
            ? `Supprimer l'immeuble « ${pendingDelete.nom} » ? Cette action est définitive.`
            : ""
        }
        blockedMessage={
          pendingBiens > 0
            ? `Cet immeuble possède ${pendingBiens} bien${pendingBiens > 1 ? "s" : ""} rattaché${
                pendingBiens > 1 ? "s" : ""
              }. Supprimez ou déplacez d'abord ces biens pour éviter des données orphelines.`
            : undefined
        }
      />
    </div>
  );
}